/**
 * @file: useUserProfileInfo.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useAuthContext } from '@euler/app/flows/auth/context';
import { useCachedQuery } from '@euler/functions/useCachedQuery';
import { UserProfile } from '@euler/model';
import { useServiceFactory } from '@euler/services/factory';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { withUserProfile } from './withUserProfile';

export const useUserProfileInfo = (): UserProfile | undefined => {
  const { authState$ } = useAuthContext();
  const [authState, setAuthState] = useBehaviorSubject(authState$);
  const { userService } = useServiceFactory();
  const uid = authState.isAuthenticated
    ? authState.authenticatedUserInfo.user.uid
    : undefined;

  const { state: userProfileInfo } = useCachedQuery<UserProfile>(
    'user-profile-info.json',
    __DEV__ ? 1 : 300,
    async () => {
      return await userService
        .getUserProfileInfo()
        .then(res => {
          setAuthState(state => withUserProfile(state, res));
          return res;
        })
        .catch(err => {
          console.error(err);
          return undefined;
        });
    },
    true,
    {
      initialState: authState.isAuthenticated
        ? authState.authenticatedUserInfo
        : undefined,
      validate: info => info.user.uid === uid,
    },
  );

  return userProfileInfo;
};
